import { useCallback, useEffect } from 'react'

const defaultMessage = 'Changes that you made may not be saved.'

/**
 * Hook that prompts the user to confirm before leaving the page.
 *
 * @param shouldConfirm - A boolean that, when true, will cause the confirmation dialog to appear.
 * @param message - (Optional) Custom message for the confirmation dialog. Defaults to a standard message.
 *
 * Note: Modern browsers might not display this message due to security reasons.
 * Instead, they will display a standard message provided by the browser itself.
 */
export const useConfirmBeforeUnload = (
  shouldConfirm: boolean,
  message: string = defaultMessage
) => {
  const handleBeforeUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      if (!shouldConfirm) {
        return
      }

      event.returnValue = message
      return message
    },
    [shouldConfirm, message]
  )

  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [handleBeforeUnload])
}
